/*eslint-disable*/
import AddIcon from '@mui/icons-material/Add';
import BackupOutlinedIcon from '@mui/icons-material/BackupOutlined';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import { TabContext, TabPanel } from '@mui/lab';
import {
  Box,
  Button,
  Link,
  Paper,
  styled,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography
} from '@mui/material';
import { format } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import * as yup from 'yup';
import DefaultTable from '../../components/DefaultTable/DefaultTable';
import TableToolbar from '../../components/DefaultTable/TableToolbar/TableToolbar';
import FormatCurrency from '../../components/FormatNumber/FormatCurrency';
import GlobalSearch from '../../components/GlobalSearch/GlobalSearch';
import PageTitle from '../../components/PageTitle/PageTitle';
import { PaymentStatus } from '../../enums/paymentStatus';
import { useShowFilterOrSearch } from '../../hooks/useShowFilterOrSearch';
import { useTableFunctions } from '../../hooks/useTableFunctions';
import { HeadCell } from '../../models/headCell';
import { Pagination } from '../../models/pagination';
import { Payment } from '../../models/payment';
import { QuickPayment } from '../../models/quickPayment';
import { Talent } from '../../models/talent';
import { paymentService } from '../../services/paymentService';
import { talentService } from '../../services/talentService';
import {
  createDownloadFile,
  removeEmptyObjectFields
} from '../../utils/helpers';
import { getRoundStatusStyle } from '../../utils/statusRoundStyle';
import DuplicateModal from './DuplicateModal';
import FilterPayments, { FilterPaymentsForm } from './FilterPayments';
import LoadingFilesModal from './LoadingFilesModal';
import PaymentRow from './PaymentRow';
import SuccessPaymentModal from './SuccessPaymentModal';

const StyledTab = styled(Tab)({
  borderRadius: '7px 7px 0 0 ',
  '&.Mui-selected': {
    backgroundColor: 'white'
  },
  '&.Mui-focusVisible': {
    backgroundColor: 'white'
  }
});

const initialRows = [
  {
    id: null,
    poNumber: null,
    paymentDate: null,
    talent: null,
    description: null,
    category: null,
    expenseCode: null,
    payType: null,
    address: null,
    quantity: null,
    hourlyRate: null,
    totalAmount: null
  },
  {
    id: null,
    poNumber: null,
    paymentDate: null,
    talent: null,
    description: null,
    category: null,
    expenseCode: null,
    payType: null,
    address: null,
    quantity: null,
    hourlyRate: null,
    totalAmount: null
  },
  {
    id: null,
    poNumber: null,
    paymentDate: null,
    talent: null,
    description: null,
    category: null,
    expenseCode: null,
    payType: null,
    address: null,
    quantity: null,
    hourlyRate: null,
    totalAmount: null
  },
  {
    id: null,
    poNumber: null,
    paymentDate: null,
    talent: null,
    description: null,
    category: null,
    expenseCode: null,
    payType: null,
    address: null,
    quantity: null,
    hourlyRate: null,
    totalAmount: null
  },
  {
    id: null,
    poNumber: null,
    paymentDate: null,
    talent: null,
    description: null,
    category: null,
    expenseCode: null,
    payType: null,
    address: null,
    quantity: null,
    hourlyRate: null,
    totalAmount: null
  }
];

const schema = yup.array().of(
  yup.object({
    poNumber: yup.string().nullable(),
    paymentDate: yup.string().required(),
    talent: yup.object().required(),
    description: yup.string().required(),
    category: yup.string().required(),
    expenseCode: yup.string().nullable(),
    payType: yup.string().nullable(),
    quantity: yup.number().nullable(),
    hourlyRate: yup.number().nullable(),
    totalAmount: yup.number().required()
  })
);

const CreatePayment = (): JSX.Element => {
  const initialFilterData: FilterPaymentsForm = {
    poNumber: '',
    talents: [],
    startDate: null,
    endDate: null,
    category: '',
    address: '',
    paymentStatuses: []
  };
  const [showFilters, showSearch, onFilterClick, onSearchClick] =
    useShowFilterOrSearch();
  const [value, setValue] = useState('1');
  const [rows, setRows] = useState<QuickPayment[]>(initialRows);
  const [submittedNumber, setSubmittedNumber] = useState(0);
  const [payments, setPayments] = useState<Pagination<Payment> | undefined>(
    undefined
  );
  const [talents, setTalents] = useState<Talent[]>([]);
  const [duplicates, setDuplicates] = useState<QuickPayment[]>([]);
  const [duplicatedRows, setDuplicatedRows] = useState<QuickPayment[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [successModal, setSuccessModal] = useState(false);
  const ref = React.createRef<HTMLInputElement>();
  const [errors, setErrors] = useState([]);
  const [remoteValidation] = useState(false);
  const [indexes, clearIndex] = useState(Date.now());
  const [, updateState] = React.useState({});
  const forceUpdate = React.useCallback(() => updateState({}), []);
  const {
    columnsData,
    setColumnsData,
    page,
    rowsPerPage,
    sortBy,
    sortType,
    searchTerm,
    filterData,
    onRequestSort,
    onChangePage,
    onChangeRowsPerPage,
    onChangeColumnVisibility,
    onSearchChange,
    onFilterChange
  } = useTableFunctions<FilterPaymentsForm>([], initialFilterData);
  const initialTalentsColumnsData: HeadCell[] = [
    {
      id: 'poNumber',
      label: 'PO#',
      sortable: true,
      visible: true,
      render: (quickPayment) => quickPayment.poNumber
    },
    {
      id: 'paymentDate',
      label: 'Date',
      sortable: true,
      visible: true,
      render: (quickPayment: QuickPayment) =>
        quickPayment.paymentDate &&
        format(new Date(quickPayment.paymentDate), 'MMM do, Y')
    },
    {
      id: 'paidTo.firstName',
      label: 'Payee',
      sortable: true,
      visible: true,
      render: (quickPayment) =>
        quickPayment.user.firstName + ' ' + quickPayment.user.lastName
    },
    {
      id: 'description',
      label: 'Description',
      sortable: true,
      visible: true,
      render: (quickPayment) => quickPayment.description
    },
    {
      id: 'category',
      label: 'Category',
      sortable: true,
      visible: true,
      render: (quickPayment) => quickPayment.category
    },
    {
      id: 'expenseCode',
      label: 'Expense',
      sortable: true,
      visible: true,
      render: (quickPayment) => quickPayment.expenseCode
    },
    {
      id: 'address',
      label: 'Location',
      sortable: true,
      visible: true,
      render: (quickPayment: QuickPayment) => {
        return quickPayment.address;
      }
    },
    {
      id: 'quantity',
      label: 'Qty',
      sortable: true,
      visible: true,
      render: (quickPayment) => quickPayment.quantity
    },
    {
      id: 'hourlyRate',
      label: 'Hourly',
      sortable: true,
      visible: true,
      render: (quickPayment) =>
        quickPayment.hourlyRate && (
          <FormatCurrency value={quickPayment.hourlyRate} />
        )
    },
    {
      id: 'totalAmount',
      label: 'Total',
      sortable: true,
      visible: true,
      render: (quickPayment) => (
        <FormatCurrency value={quickPayment.totalAmount} />
      )
    },
    {
      id: 'paymentStatus',
      label: 'Status',
      sortable: true,
      thStyle: { width: 200, textAlign: 'center' },
      tdStyle: { textAlign: 'center' },
      visible: true,
      render: (quickPayment: QuickPayment) =>
        quickPayment.paymentStatus && (
          <Box
            sx={{
              ...getRoundStatusStyle(PaymentStatus[quickPayment.paymentStatus])
            }}
          >
            {PaymentStatus[quickPayment.paymentStatus]}
          </Box>
        )
    }
  ];

  useEffect(() => {
    setColumnsData(initialTalentsColumnsData);
  }, [talents]);

  const generateFilterBody = () => {
    let body = removeEmptyObjectFields(filterData);
    if (body.talents) {
      body = {
        ...body,
        talents: filterData.talents
          ? filterData.talents.map((talent) => talent.id)
          : []
      };
    }
    if (searchTerm) {
      body = {
        ...body,
        searchTerm: searchTerm
      };
    }

    return body;
  };

  const handleExport = () => {
    paymentService.exportPayments(generateFilterBody()).then((response) => {
      createDownloadFile(response.data, 'payments');
    });
  };

  const testing = useMemo(
    () =>
      rows.map((item, index) => (
        <PaymentRow
          forwardRef={ref}
          key={index}
          item={item}
          remoteValidate={remoteValidation}
          rows={rows}
          checkDuplicates={() => {
            checkDuplicatedRows(rows);
            checkDuplicates(rows);
          }}
          // duplicates tell you multiple rows with same talent
          duplicates={duplicates}
          duplicatedRows={duplicatedRows}
          talents={talents}
          parentErrors={errors}
          setRows={setRows}
          index={index}
        />
      )),
    [rows, talents, errors, remoteValidation, duplicatedRows, duplicates]
  );

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  const handleDraft = () => {
    localStorage.setItem('draft', JSON.stringify(rows));
  };

  useEffect(() => {
    talentService.getTalentList({ params: { active: true } }).then((e) => {
      setTalents(e.data);
    });
    setRows([...initialRows]);
    clearIndex(Date.now());
    const draftItems = localStorage.getItem('draft');
    setTimeout(() => {
      if (draftItems) {
        const draftItemsConverted = JSON.parse(draftItems);
        setRows(draftItemsConverted);
      }
    }, 0);
    forceUpdate();
  }, []);

  const getPayments = () => {
    paymentService
      .getPayments(generateFilterBody(), {
        params: {
          page: page,
          size: rowsPerPage,
          sort: `${sortBy},${sortType}`
        }
      })
      .then((e) => {
        setPayments(e.data);
      });
  };

  useEffect(() => {
    getPayments();
  }, [value, page, rowsPerPage, sortBy, sortType, searchTerm, filterData]);

  useEffect(() => {
    if (rows && rows != initialRows) {
      checkDuplicates(rows);
    }
  }, [rows]);

  useEffect(() => {
    clearIndex(Date.now());
    forceUpdate();
  }, [rows.length]);

  const handleSubmitPayments = async () => {
    const testParse = rows.map(removeEmptyObjectFields);
    const testEmpty = testParse.filter((element) => {
      if (Object.keys(element).length) {
        return true;
      }
    });
    testEmpty.map((e) => (e.paymentType = 'QUICK_PAYMENT'));
    setSubmittedNumber(testEmpty.length);
    const hasDuplicates = await checkDuplicatedRows(rows);

    if (testEmpty.length != 0 && !hasDuplicates) {
      schema
        .validate(testEmpty, { abortEarly: false })
        .then(() => {
          paymentService.createPayments(testEmpty);
          setSuccessModal(true);
        })
        .catch((e) => {
          setErrors(e.inner);
        });
    } else {
      setOpen(true);
    }
  };

  function getUniqueListBy(arr: Array<any>, key: string) {
    return [...new Map(arr.map((item) => [item[key], item])).values()];
  }

  const checkDuplicates = (array: QuickPayment[]) => {
    const results: any = new Object({});
    array.forEach((element) => {
      if (element.talent) {
        const init = `${element.talent.firstName}${element.talent.lastName}${element.talent.id}`;
        const initWithoutId = init.replace(/[0-9]/g, '');
        if (!results[initWithoutId]) {
          results[initWithoutId] = [];
          results[initWithoutId].push(element);
        } else {
          results[initWithoutId].push(element);
        }
      }
    });
    Object.keys(results).forEach((key) => {
      results[key] = getUniqueListBy(results[key], 'userId');
    });
    const arr: QuickPayment[] = [];
    for (const [key, value] of Object.entries(results)) {
      const val = value as QuickPayment[];
      if (val.length > 1) {
        arr.push(...val);
      }
    }
    setDuplicates(arr);
  };

  const checkDuplicatedRows = (array: QuickPayment[]) => {
    const results: any = new Object({});
    const isEqual = (...objects: QuickPayment[]) =>
      objects.every(
        (obj) => JSON.stringify(obj) === JSON.stringify(objects[0])
      );
    array.forEach((element) => {
      if (element.talent) {
        const init = `${element.talent.firstName}${element.talent.lastName}${element.talent.id}`;
        const initWithoutId = init.replace(/[0-9]/g, '');
        if (!results[initWithoutId]) {
          results[initWithoutId] = [];
          results[initWithoutId].push(element);
        } else {
          results[initWithoutId].push(element);
        }
      }
    });

    let arr = [];

    for (let [key, value] of Object.entries(results)) {
      const val = value as QuickPayment[];
      if (isEqual(...val) && val.length > 1) {
        arr.push(...val);
      }
    }

    setDuplicatedRows(arr);
    return arr.length > 0;
  };

  const processData = (dataString: string) => {
    const dataStringLines = dataString.split(/\r\n|\n/);
    const headers = dataStringLines[0].split(
      /,(?![^"]*"(?:(?:[^"]*"){2})*[^"]*$)/
    );

    const list: QuickPayment[] = [];
    for (let i = 1; i < dataStringLines.length; i++) {
      const row = dataStringLines[i].split(
        /,(?![^"]*"(?:(?:[^"]*"){2})*[^"]*$)/
      );
      if (headers && row.length == headers.length) {
        // eslint-disable-next-line
        const obj: any = {};
        for (let j = 0; j < headers.length; j++) {
          let d = row[j];
          if (d.length > 0) {
            if (d[0] == '"') d = d.substring(1, d.length - 1);
            if (d[d.length - 1] == '"') d = d.substring(d.length - 2, 1);
          }
          if (headers[j]) {
            if (headers[j] == 'poNumber' && d == '') {
              obj[headers[j]] = undefined;
            } else if (headers[j] == 'address' && d != '') {
              obj[headers[j]] = d;
            } else if (headers[j] == 'address' && d == '') {
              obj[headers[j]] = null;
            } else if (headers[j] == 'talent') {
              const talent = talents.find(
                (e: Talent) =>
                  e.firstName.trim() + ' ' + e.lastName.trim() == d.trim()
              );
              if (talent) {
                obj[headers[j]] = talent;
                obj.userId = talent.id;
              }
            } else if (headers[j] == 'paymentDate' && d != '') {
              obj[headers[j]] = new Date(d);
            } else {
              obj[headers[j]] = d;
            }
          }
        }

        // remove the blank rows
        if (Object.values(obj).filter((x) => x).length > 0) {
          list.push(obj);
        }
      }
    }
    checkDuplicates(list);
    setRows(list);
    setLoading(false);
  };

  // eslint-disable-next-line
  const handleFileLoad = (fileList: FileList | null) => {
    if (fileList) {
      const file = fileList[0];
      const reader = new FileReader();
      setLoading(true);

      reader.onload = (evt) => {
        if (evt != null) {
          const bstr = evt?.target?.result;
          const wb = XLSX.read(bstr, { type: 'binary' });
          const wsname = wb.SheetNames[0];
          const ws = wb.Sheets[wsname];
          const data = XLSX.utils.sheet_to_csv(ws, {});
          setRows([]);
          processData(data);
        }
      };
      reader.readAsBinaryString(file);
    }
  };

  const clearAll = () => {
    localStorage.removeItem('draft');
    setRows([...initialRows]);
    setErrors([]);
    clearIndex(Date.now());
    forceUpdate();
  };

  useEffect(() => {
    setRows([...initialRows]);
    clearIndex(Date.now());
    forceUpdate();
  }, []);

  const closeSuccessModal = (e: boolean) => {
    setSuccessModal(e);
    clearAll();
  };

  return (
    <Box key={indexes} ref={ref}>
      <Box>
        <PageTitle>Create Payments</PageTitle>
        <TabContext value={value}>
          <Box>
            <Tabs
              value={value}
              onChange={handleTabChange}
              aria-label="basic tabs example"
            >
              <StyledTab label="Create Payments" value="1" />
              <StyledTab label="View All Payments" value="2" />
            </Tabs>
          </Box>
          <TabPanel
            sx={{
              background: 'white',
              p: 0,
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: 'column'
            }}
            value={'1'}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: 1,
                borderColor: 'divider'
              }}
              p={2}
            >
              <Typography variant="h5">Create Payments</Typography>

              <Box
                sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
              >
                <Box
                  // onClick={(e) => exportToCSV()}
                  sx={{ cursor: 'pointer', mr: 2, mt: 0.2 }}
                >
                  <Tooltip title="Download Template">
                    <a href="/files/quick-payment-template.xlsx" download>
                      <CloudDownloadOutlinedIcon
                        fontSize="medium"
                        sx={{
                          fontSize: 40,
                          color: '#0060C9',
                          cursor: 'pointer'
                        }}
                      />
                    </a>
                  </Tooltip>
                </Box>
                <Box sx={{ cursor: 'pointer', mr: 2, mt: 0.2 }}>
                  <label htmlFor="fileLoader">
                    <Tooltip title="Upload File">
                      <BackupOutlinedIcon
                        fontSize="medium"
                        sx={{
                          fontSize: 40,
                          color: '#0060C9',
                          cursor: 'pointer'
                        }}
                      />
                    </Tooltip>
                    <input
                      type="file"
                      id="fileLoader"
                      onChange={(event) => handleFileLoad(event.target.files)}
                      hidden
                    />
                  </label>
                </Box>
                <Link
                  underline="hover"
                  sx={{ cursor: 'pointer' }}
                  onClick={() => {
                    clearAll();
                  }}
                >
                  Clear All
                </Link>
                <Button
                  sx={{ ml: 2 }}
                  variant="outlined"
                  onClick={() => {
                    handleDraft();
                  }}
                >
                  Save as Draft
                </Button>
                <Button sx={{ ml: 2 }} onClick={handleSubmitPayments}>
                  Submit Payments
                </Button>
              </Box>
            </Box>
            <Box sx={{ overflow: 'auto', width: '100%' }}>
              <Table sx={{ minWidth: 940 }} aria-label="simple table">
                <TableHead sx={{ whiteSpace: 'nowrap' }}>
                  <TableRow>
                    <TableCell sx={{ px: '10px' }}>PO #</TableCell>
                    <TableCell sx={{ px: '10px' }}>Date *</TableCell>
                    <TableCell sx={{ px: '10px' }}>Payee Name *</TableCell>
                    <TableCell sx={{ px: '10px' }}>Description *</TableCell>
                    <TableCell sx={{ px: '10px' }}>Category *</TableCell>
                    <TableCell sx={{ px: '10px' }}>Expense Code</TableCell>
                    <TableCell sx={{ px: '10px' }} align="center">
                      Pay Type
                    </TableCell>
                    <TableCell sx={{ px: '10px' }}>Location</TableCell>
                    <TableCell sx={{ px: '10px' }}>Qty</TableCell>
                    <TableCell sx={{ px: '10px' }}>Hourly Rate</TableCell>
                    <TableCell sx={{ px: '10px' }}>Total Amount *</TableCell>
                    <TableCell sx={{ px: '10px' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>{testing}</TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={6} sx={{ px: 1 }}>
                      <Box>
                        <Button
                          sx={{ width: '200px', ml: 1 }}
                          onClick={() => {
                            const aux = rows;
                            let control = 0;

                            while (control < 10) {
                              aux.push({
                                id: null,
                                poNumber: null,
                                paymentDate: null,
                                talent: null,
                                description: null,
                                category: null,
                                expenseCode: null,
                                payType: null,
                                address: null,
                                quantity: null,
                                hourlyRate: null,
                                totalAmount: null
                              });
                              setRows([...aux]);
                              control++;
                            }
                          }}
                        >
                          <AddIcon sx={{ mr: 1 }} /> Add 10 Rows
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </Box>
          </TabPanel>
          <TabPanel
            sx={{
              background: 'white',
              p: 0,
              minWidth: '940px'
            }}
            value={'2'}
          >
            <TableContainer component={Paper} sx={{ minHeight: '320px' }}>
              <TableToolbar
                viewTableColumnsProps={{
                  columns: columnsData,
                  onColumnClick: onChangeColumnVisibility
                }}
                onFilter={onFilterClick}
                onSearch={onSearchClick}
                onExport={handleExport}
              >
                {showSearch && (
                  <GlobalSearch
                    onSearchChange={onSearchChange}
                    searchTerm={searchTerm}
                  />
                )}
                {showFilters && (
                  <FilterPayments
                    handleFormSubmit={onFilterChange}
                    filterData={filterData}
                  />
                )}
              </TableToolbar>
              <DefaultTable
                columns={columnsData}
                data={payments?.content}
                sortProps={{ sortBy, sortType, onRequestSort }}
                paginationProps={{
                  page,
                  rowsPerPage,
                  onChangePage,
                  onChangeRowsPerPage,
                  totalElements: payments?.totalElements
                }}
              />
              {!payments?.content.length && (
                <Typography variant="h5" align="center" m={4}>
                  No payments found!
                </Typography>
              )}
            </TableContainer>
          </TabPanel>
        </TabContext>
        <LoadingFilesModal open={loading} setOpen={setLoading} />
        <SuccessPaymentModal
          open={successModal}
          setOpen={closeSuccessModal}
          payments={submittedNumber}
        />
        <DuplicateModal duplicates={duplicates} open={open} setOpen={setOpen} />
      </Box>
    </Box>
  );
};

export default CreatePayment;
